import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  getProviderModels,
  refreshProviderModels,
  type ProviderModelsResponse,
} from '../api';

/**
 * Reads the cached model catalog for a single platform provider. Enabled by default; pass
 * <c>options.enabled: false</c> to defer the fetch until the admin clicks "Cargar modelos".
 */
export function useProviderModels(
  providerId: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<ProviderModelsResponse> {
  return useQuery({
    queryKey: providerId ? AdminQueryKeys.providerModels(providerId) : ['admin', 'platform-providers', '(none)', 'models'],
    queryFn: ({ signal }) => getProviderModels(providerId as string, signal),
    enabled: Boolean(providerId) && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}

/** Forces a re-fetch ignoring the 24h cache. Used by the "Refrescar" button on the form. */
export function useRefreshProviderModels(): ReturnType<
  typeof useMutation<ProviderModelsResponse, Error, string>
> {
  const queryClient = useQueryClient();
  return useMutation<ProviderModelsResponse, Error, string>({
    mutationFn: (providerId) => refreshProviderModels(providerId),
    onSuccess: (data) => {
      queryClient.setQueryData(AdminQueryKeys.providerModels(data.providerId), data);
    },
  });
}
